import { z } from 'zod';
import { RoleRefSchema } from '../../rbac/index.js';
import { AuditAction, RequestStatus } from './status.js';

/**
 * @aiPurpose wire projection of a user authoring a chat message or
 *   acting on an audit entry. Reusable across any module that surfaces
 *   "who did this": carries the actor's stable id, current display
 *   name, and current role projection (`RoleRefSchema`). Live join, not
 *   snapshot — the role reflects the user's role at read time.
 * @aiInvariant `id` is the actor's User UUID; `name` is the live
 *   `User.name`; `role` is the live `RoleRef` projection. Schema is
 *   `.strict()` — extra keys reject. Consumers pick UI labels via the
 *   shared `roleLabel` helper (web) which folds the legacy `'logist'`
 *   machine name into the `logist_cn` UI bucket and falls back to
 *   `role.displayName` for custom roles.
 * @aiPii surfaces actor name — redact in logs.
 */
export const UserActorRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    role: RoleRefSchema,
  })
  .strict();
export type UserActorRef = z.infer<typeof UserActorRefSchema>;

// INVARIANT (#570): `readByCounterparty` is server-computed per message
//   for the request-chat read-receipt contract. Optional on the wire so
//   stale browser bundles built before the expand do not reject the new
//   key, AND so any future read path that does not project the boolean
//   (e.g. a backfill tool) keeps round-tripping. Backend MUST always
//   populate a strict boolean for the live chat endpoints
//   (`GET /requests/:id/chat`, `POST /requests/:id/chat`) — `false` when
//   not applicable (non-own messages, former-manager/former-logist
//   messages, admin/observer-authored messages, DRAFT/no-counterparty,
//   counterparty not yet read).
//
// @aiPii reveals that the counterparty has viewed the chat tail; the
//   exact `lastReadAt` timestamp is NEVER exposed to the wire (only the
//   derived boolean lands here). Do not widen to a timestamp without a
//   separate threat-model review.
// @aiSecurity the boolean is derived against the request's CURRENT
//   `{managerId, logistId}` pair and the counterparty's stored
//   `lastReadAt`. Reassignment recalculates it; there is no sticky
//   per-message "someone was once counterparty-read" state.
export const ChatMessageSchema = z
  .object({
    id: z.string().min(1),
    by: UserActorRefSchema,
    at: z.string().datetime(),
    text: z.string().min(1),
    readByCounterparty: z.boolean().optional(),
  })
  .strict();
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const AuditEntrySchema = z
  .object({
    at: z.string().datetime(),
    // INVARIANT: `null` when the actor User has been hard-deleted
    //   (`RequestAuditEntry.actorId` is FK SetNull). Frontend renders
    //   the localized `audit.actorDeleted` placeholder in that case
    //   and hides the role pill. Backend MUST NOT emit a literal
    //   '(deleted)' string in this slot.
    by: UserActorRefSchema.nullable(),
    text: z.string(),
    action: z.nativeEnum(AuditAction),
    from: z.nativeEnum(RequestStatus).optional(),
    to: z.nativeEnum(RequestStatus).optional(),
  })
  .strict();
export type AuditEntry = z.infer<typeof AuditEntrySchema>;
